'use client';

import React, { useState } from 'react';
import useMagicAuthSavedEmail from 'src/hooks/useMagicAuthSavedEmail';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { notify } from 'src/utils/notifications';
import { MagicAuthLoginOptions } from 'src/utils/magicAuthAdapter';
import { ONBOARDING_CONNECTION_STEP_LOCK_KEY } from 'src/utils/WalletConnectionState';
import { DRIFT_CUSTOM_WALLET_OPTIONS } from 'src/constants/wallets';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import Link from 'src/components/primitives/Link/Link';
import Input from 'src/components/primitives/Input/Input';
import Button from 'src/components/primitives/Button/Button';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import { useDisconnectWallets } from 'src/hooks/wallets/useDisconnectWallets';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import useConnectCustomWallet from 'src/hooks/wallet-management/useConnectCustomWallet';

export interface MagicAuthLoginPageProps {
	onBackFromMagicAuth: () => void;
}

const MagicAuthLoginPage = (props: MagicAuthLoginPageProps) => {
	const { connectWithCustomWallet } = useConnectCustomWallet();
	const connected = useWalletIsConnected();
	const [magicAuthSavedEmail, setMagicAuthSavedEmail] =
		useMagicAuthSavedEmail();
	const [emailAddress, setEmailAddress] = useState(magicAuthSavedEmail);
	const [isCreateAccount, setIsCreateAccount] = useState(false);
	const { captureEvent } = usePostHogCapture();
	const walletConnectionState = useCurrentWalletConnectionState();

	const disconnectWallets = useDisconnectWallets();

	const handleClickSignUp = (e: React.MouseEvent) => {
		e.preventDefault();
		setIsCreateAccount(true);
	};

	const handleMagicAuthLogin = async (loginOptions: MagicAuthLoginOptions) => {
		if (connected) {
			await disconnectWallets();
		}

		try {
			walletConnectionState.instance.update(
				'Connecting',
				true,
				ONBOARDING_CONNECTION_STEP_LOCK_KEY
			);

			await connectWithCustomWallet(
				DRIFT_CUSTOM_WALLET_OPTIONS.MAGIC_AUTH_WALLET_ADAPTER_NAME,
				loginOptions
			);

			props.onBackFromMagicAuth();

			if (loginOptions.email) {
				setMagicAuthSavedEmail(emailAddress);
			}

			captureEvent('magic_auth_login', {
				auth_type: loginOptions.email ? 'email' : 'other',
				success: true,
			});
		} catch (e: any) {
			const errorMatches = `${e?.message}`.match(
				/Magic RPC Error: \[(.*)\] (.*)/
			);

			console.error(e);

			walletConnectionState.instance.update(
				'Connecting',
				false,
				ONBOARDING_CONNECTION_STEP_LOCK_KEY
			);

			captureEvent('magic_auth_login', {
				auth_type: loginOptions.email ? 'email' : 'other',
				success: false,
			});

			if (errorMatches?.length) {
				notify({
					type: 'error',
					message: 'Error connecting to Magic Link',
					description: `${errorMatches[2]} (Error Code: ${errorMatches[1]})`,
				});
			} else {
				notify({
					type: 'error',
					message: 'Error connecting to Magic Link',
				});
			}
		}
	};

	return (
		<div className="flex flex-col gap-y-2xl">
			{isCreateAccount && (
				<>
					<p className="typo-b4 text-text-secondary">
						Magic Wallet automatically created a Solana wallet for you
						that&apos;s compatible with Drift. You&apos;ll be able to interact
						with this wallet via Drift&apos;s UI.
					</p>
					<p className="typo-b4 text-text-secondary">
						Drift doesn&apos;t store your private keys and cannot recover your
						wallet on your behalf.{' '}
						<Link
							href="https://docs.drift.trade/getting-started/passwordless-login"
							openInNewTab
						>
							Learn more
						</Link>
						.
					</p>
				</>
			)}

			<div className="flex flex-col gap-y-md">
				<h3 className="typo-b3 text-text-default">
					{isCreateAccount ? 'Sign Up' : 'Passwordless Login'}
				</h3>
				<Input
					id="magic-auth-email-input"
					type="email"
					label="Your Email"
					labelVariant="standard"
					value={emailAddress}
					onChange={setEmailAddress}
				/>

				<BaseModal.Footer className="-mx-lg flex flex-col items-stretch pb-0">
					<Button
						size="lg"
						className="w-full"
						onClick={() => handleMagicAuthLogin({ email: emailAddress })}
						disabled={!emailAddress}
					>
						{isCreateAccount ? 'Sign Up' : 'Log In'}
					</Button>
					<div className="text-center text-text-secondary">
						<span className="typo-b4">
							Don&apos;t have an account?{' '}
							<TextButton
								size="sm"
								variant="tertiary"
								onClick={handleClickSignUp}
							>
								Sign Up
							</TextButton>
						</span>
					</div>
				</BaseModal.Footer>
			</div>
		</div>
	);
};

export default MagicAuthLoginPage;
